/**
 * AskBot knowledge & retrieval layer
 * ----------------------------------
 * Powers the Education Helper (EducationAskBot). Two layers, both indexed with
 * Fuse.js for fuzzy, typo-tolerant, natural-language matching:
 *
 *   1. TOPICS  — intent routing. Broad, human-worded answers that point people
 *      to the right page (jobs, special education, child protection, careers,
 *      scholarships, exams, enrolment, second-chance, …).
 *   2. CORPUS  — the site's current data (scholarships, grants, loans, JAMVAT,
 *      study-abroad, tertiary institutions) so a question like "money for a
 *      nursing degree" surfaces real, live records.
 *
 * answerQuery(q) returns a response object the UI already understands
 * ({ type, title, answer, tips?, steps?, link?, results?, related?, disclaimer? }).
 */
import Fuse from 'fuse.js';
import scholarshipsData from '../data/scholarships-jamaica.json';
import tertiary from '../data/tertiary-institutions.json';

// ---------------------------------------------------------------------------
// 1) TOPICS — natural-language intent routing with a warm, human tone
// ---------------------------------------------------------------------------
export const TOPICS = [
  {
    id: 'child-protection',
    keywords: 'child protection safety abuse report abuse child abuse bullying neglect harm children advocate OCA CDA hurt unsafe protect my child domestic violence molest predator',
    type: 'warning',
    title: 'Child Protection & Safety',
    answer: "If you're worried about a child's safety, reaching out is exactly the right thing to do. Here's how to get help and report concerns in Jamaica.",
    tips: [
      'Emergency / immediate danger: call the Police at 119',
      'National Children\'s Registry (report abuse): 1-888-PROTECT (1-888-776-8328)',
      'Office of the Children\'s Advocate for advice and rights',
      'Child Protection and Family Services Agency (CPFSA) can help',
      'Schools have a duty to act on safeguarding concerns — speak to a guidance counsellor'
    ],
    page: 'child-protection',
    link: { text: 'Child Protection resources', page: 'child-protection' }
  },
  {
    id: 'special-education',
    keywords: 'special education special needs learning disability disabilities dyslexia autism ADHD adhd slow learner remedial extra time exam accommodation accommodations IEP struggling to learn difficulty reading wheelchair hearing impaired visually impaired deaf blind inclusive',
    type: 'info',
    title: 'Special Education & Learning Support',
    answer: "Every child learns differently, and there's real support out there. If your child has additional or special educational needs, here's where to start.",
    tips: [
      'CSEC/CAPE exam accommodations (extra time, readers) can be arranged through CXC',
      'Some schools have special education units and trained support staff',
      'Early assessment helps — ask for an educational psychologist referral',
      'The Ministry of Education\'s Special Education Unit can advise on placement',
      'You are your child\'s best advocate — keep records of assessments'
    ],
    page: 'special-education',
    link: { text: 'Special Education Support', page: 'special-education' }
  },
  {
    id: 'education-jobs',
    keywords: 'job jobs work employment vacancy vacancies teaching job teacher job hiring career in teaching work in a school get a job education sector principal vice principal guidance counsellor teacher aide',
    type: 'info',
    title: 'Jobs in Education',
    answer: "Looking for work in education? Here's where teaching and school-support roles are posted, plus what you'll usually need to apply.",
    tips: [
      'Teaching posts usually need a B.Ed or a degree plus teacher training (JBTE)',
      'Register with the Jamaica Teaching Council (JTC)',
      'Watch Ministry of Education and individual school vacancy notices',
      'Teacher-aide and support roles have lower entry requirements',
      'HEART/NSTA certification helps for technical and vocational teaching'
    ],
    page: 'education-jobs',
    link: { text: 'Education Jobs', page: 'education-jobs' }
  },
  {
    id: 'career-path',
    keywords: 'career careers what job profession become a doctor nurse engineer lawyer pilot teacher accountant what can i do with my subjects career planning future job which career path job market what to study for',
    type: 'info',
    title: 'Careers & Pathways',
    answer: "Not sure which direction to take? Let's connect your subjects and interests to real careers — and the qualifications each one needs.",
    tips: [
      'Different careers need different CSEC/CAPE subject combinations',
      'Our Career Explorer maps subjects → study → jobs and salaries',
      'It\'s never too early (or too late) to plan a pathway',
      'Consider demand in the Jamaican job market, not just interest',
      'Vocational routes (HEART) are just as valid as university'
    ],
    page: 'career-path',
    link: { text: 'Explore Career Paths', page: 'career-path' }
  },
  {
    id: 'funding',
    keywords: 'scholarship scholarships financial aid bursary grant grants loan loans sponsorship help pay tuition help funding money for school cannot afford fees afford school pay for university student loan JAMVAT SLB PATH free money',
    type: 'info',
    title: 'Scholarships, Grants & Financial Aid',
    answer: "Paying for school can feel overwhelming, but there are more options than most people realise — scholarships, grants, loans and vouchers. Here are some that may fit.",
    tips: [
      'Scholarships (merit or need-based) — you don\'t pay these back',
      'Grants like PATH help with school expenses for eligible families',
      'The Students\' Loan Bureau (SLB) offers low-interest tertiary loans',
      'JAMVAT can cover part of tertiary tuition',
      'Apply early and to several — deadlines vary through the year'
    ],
    page: 'funding',
    link: { text: 'Scholarships & Funding', page: 'funding' }
  },
  {
    id: 'side-hustles',
    keywords: 'side hustle side hustles make money earn money as a student part time small business entrepreneurship sell online extra cash student income',
    type: 'info',
    title: 'Student Side Hustles',
    answer: "Want to earn a little on the side while you study? There are legit ways for Jamaican students to make money — here's some inspiration.",
    tips: [
      'Tutoring younger students in subjects you\'re strong in',
      'Freelancing (design, writing, social media) online',
      'Small-scale reselling or handmade goods',
      'Balance earning with your studies — school comes first',
      'Keep it legal and safe'
    ],
    page: 'side-hustles',
    link: { text: 'Student Side Hustles', page: 'side-hustles' }
  },
  {
    id: 'summer-employment',
    keywords: 'summer job summer employment holiday job internship work experience vacation job summer work intern',
    type: 'info',
    title: 'Summer Jobs & Internships',
    answer: "Summer is a great time to gain experience and earn. Here's where students find seasonal jobs and internships in Jamaica.",
    tips: [
      'HEART/NSTA HOPE programme places youth in work experience',
      'Corporate summer internships open around March–May',
      'Government summer employment programmes run each year',
      'Start applying early — spots fill quickly'
    ],
    page: 'summer-employment',
    link: { text: 'Summer Employment', page: 'summer-employment' }
  },
  {
    id: 'opportunity-matcher',
    keywords: 'match opportunities find opportunities for me what am i eligible for recommend opportunities personalised suggestions what can i apply for',
    type: 'info',
    title: 'Opportunity Matcher',
    answer: "Not sure what you qualify for? The Opportunity Matcher lines up scholarships, internships and programmes with your profile.",
    tips: [
      'Tell it your level, interests and situation',
      'Get a shortlist tailored to you',
      'A quick way to cut through the noise'
    ],
    page: 'opportunity-matcher',
    link: { text: 'Try the Opportunity Matcher', page: 'opportunity-matcher' }
  },
  {
    id: 'second-chance',
    keywords: 'dropped out drop out second chance adult education mature student go back to school never finished HEART evening classes night school returning to study missed school no CSEC start over restart education',
    type: 'info',
    title: 'Second-Chance Education',
    answer: "It's genuinely never too late to continue your education. Whether you left school early or want to upskill, there are real routes back in.",
    tips: [
      'HEART/NSTA offers free training — no CSEC needed for many programmes',
      'Sit CSEC as a private candidate at any age',
      'Evening and part-time classes fit around work',
      'Mature-student entry (usually 21+/23+) opens university without CAPE',
      'You are not behind — you\'re starting from where you are'
    ],
    page: 'second-chance',
    link: { text: 'Second-Chance Education', page: 'second-chance' }
  },
  {
    id: 'tertiary',
    keywords: 'university universities college colleges UWI UTech NCU UCC degree higher education tertiary bachelor masters mona utech northern caribbean community college where to study after high school',
    type: 'info',
    title: 'Universities & Colleges',
    answer: "Ready for the next step? Jamaica has a strong range of universities and colleges. Here's how to explore programmes, entry requirements and fees.",
    tips: [
      'UWI Mona and UTech are the largest public universities',
      'Community colleges offer more affordable associate degrees',
      'Check entry requirements (usually CAPE units or an associate degree)',
      'Mature-student entry is available at most institutions',
      'SLB loans and JAMVAT can help with tuition'
    ],
    page: 'tertiary',
    link: { text: 'Universities & Colleges', page: 'tertiary' }
  },
  {
    id: 'primary',
    keywords: 'primary school prep school basic school infant school early childhood kindergarten preschool grade 1 choosing a prep school young child starting school first school',
    type: 'info',
    title: 'Primary & Prep Schools',
    answer: "Choosing where your little one starts is a big decision. Here's a guide to early-childhood, basic, infant, primary and prep schools.",
    tips: [
      'Early childhood: basic and infant schools (ages 3–5)',
      'Primary/prep: Grades 1–6, leading up to PEP',
      'Prep schools are private and fee-paying; primary schools are public',
      'Look at location, class sizes and PEP preparation'
    ],
    page: 'primary',
    link: { text: 'Primary & Prep Schools', page: 'primary' }
  },
  {
    id: 'enrollment',
    keywords: 'enroll enrol register registration admission how to get into school apply to school transfer school change school documents needed to enroll school placement start at a new school',
    type: 'procedure',
    title: 'School Enrolment',
    answer: "Getting a child into school has a few clear steps. Here's what the process looks like and what you'll need to have ready.",
    steps: [
      'Gather the birth certificate and immunisation records',
      'Get reports/transcripts from the previous school (if transferring)',
      'Contact the school(s) directly about places and application dates',
      'Submit documents and attend any interview or entrance test',
      'Wait for the acceptance/placement letter'
    ],
    tips: [
      'Apply to more than one school where you can',
      'Transfers need a release letter from the current school'
    ],
    page: 'enrollment',
    link: { text: 'Full Enrolment Guide', page: 'enrollment' }
  },
  {
    id: 'rankings',
    keywords: 'best schools top schools school rankings good schools which school is best highest performing schools league table best high school',
    type: 'info',
    title: 'School Rankings',
    answer: "Rankings can be a helpful starting point, but they're only one piece of the puzzle — the right school depends on your child, too.",
    tips: [
      'Rankings reflect one year\'s public data, not a school\'s whole story',
      'Small differences between schools are often not meaningful',
      'Look at subject strengths, support services and culture',
      'Visit on an open day to get a real feel'
    ],
    page: 'rankings',
    link: { text: 'View School Rankings', page: 'rankings' },
    disclaimer: 'Rankings are informational only — we encourage families to weigh many factors.'
  },
  {
    id: 'compare-schools',
    keywords: 'compare schools compare two schools which school is better side by side school comparison versus difference between schools',
    type: 'info',
    title: 'Compare Schools',
    answer: "Torn between a few schools? You can line them up side by side and compare what matters to you.",
    tips: [
      'Compare CSEC/CAPE performance, facilities and extracurriculars',
      'Factor in location and commute',
      'Consider fees and support services'
    ],
    page: 'compare-schools',
    link: { text: 'Compare Schools', page: 'compare-schools' }
  },
  {
    id: 'find-schools',
    keywords: 'find a school schools near me schools in kingston schools in my parish list of schools school directory high schools secondary schools search schools which schools are in',
    type: 'info',
    title: 'Find a School',
    answer: "Looking for schools in a particular parish or type? You can search the full directory and filter to what fits.",
    tips: [
      'Browse by parish across all 14 parishes',
      'Filter by school type and performance',
      'Open any school for full details'
    ],
    page: 'schools',
    link: { text: 'Search Schools', page: 'schools' }
  },
  {
    id: 'services',
    keywords: 'tutor tutoring extra lessons lessons exam prep private lessons help studying uniform school supplies bookstore education services find a tutor after school lessons',
    type: 'info',
    title: 'Tutoring & Education Services',
    answer: "Need a tutor, exam-prep class or school supplies? Our services directory lists providers across the island.",
    tips: [
      'Find tutors by subject and parish',
      'CSEC/CAPE exam-prep centres',
      'Uniform and school-supply providers',
      'Read reviews before you book'
    ],
    page: 'services',
    link: { text: 'Find Services & Tutors', page: 'services' }
  },
  {
    id: 'extracurriculars',
    keywords: 'sports clubs extracurricular activities champs boys champs girls champs manning cup dacosta cup football cricket netball music band drama debate cadets after school activities',
    type: 'info',
    title: 'Sports, Clubs & Activities',
    answer: "School isn't only about exams. Sports, clubs and the arts build confidence and open doors — here's what to look for.",
    tips: [
      'Athletics (Champs), football (Manning/DaCosta Cup) and more',
      'Debate, music, drama and cadet programmes',
      'Extracurriculars strengthen university and scholarship applications'
    ],
    page: 'extracurriculars',
    link: { text: 'Extracurricular Activities', page: 'extracurriculars' }
  },
  {
    id: 'how-school-works',
    keywords: 'education system how school works school levels grades stages of education how does school work in jamaica structure of education ministry of education',
    type: 'info',
    title: 'How School Works in Jamaica',
    answer: "New to how the Jamaican system fits together? Here's the big picture, from early childhood all the way to university.",
    tips: [
      'Early childhood (3–5) → Primary (6–12)',
      'Secondary (12–17) → Sixth form (17–18)',
      'Tertiary: universities, colleges and HEART',
      'The Ministry of Education (MOEY) oversees it all'
    ],
    page: 'how-school-works',
    link: { text: 'How School Works', page: 'how-school-works' }
  },
  // ---- Exams -----------------------------------------------------------
  {
    id: 'exams-overview',
    keywords: 'exam exams examination which exams cxc caribbean exams tests external exams exam guide',
    type: 'info',
    title: 'Exams in Jamaica',
    answer: "Jamaica has a few key exams at different stages. Here's a quick map of them and where to dig into each one.",
    tips: [
      'PEP — end of primary school (Grade 6)',
      'CSEC — end of secondary (Grade 11)',
      'CAPE — sixth form (Grades 12–13)',
      'NVQ-J & City & Guilds — vocational',
      'SAT — for US university applications'
    ],
    page: 'exams',
    link: { text: 'Exam Hub', page: 'exams' }
  },
  {
    id: 'exams-pep',
    keywords: 'pep primary exit profile grade 6 exam high school placement GSAT ability test performance task curriculum based test',
    type: 'info',
    title: 'PEP (Primary Exit Profile)',
    answer: "PEP is how Grade 6 students are placed into high school. It replaced GSAT in 2019 and has three parts across the year.",
    tips: [
      'Performance Task — applied problem-solving (February)',
      'Ability Test — reasoning and logic (March)',
      'Curriculum-Based Test — subject knowledge (March)',
      'Results guide the high-school placement',
      'Practising past papers really helps'
    ],
    page: 'exams-pep',
    link: { text: 'PEP Exam Guide', page: 'exams-pep' }
  },
  {
    id: 'exams-csec',
    keywords: 'csec cxc secondary exam grade 11 form 5 subjects passes what is csec csec grades sba',
    type: 'info',
    title: 'CSEC',
    answer: "CSEC is the main secondary-school leaving exam, usually sat in Grade 11. Most students take 5–8 subjects, including English and Maths.",
    tips: [
      'Held May–June each year',
      'Grades I (highest) to VI; I–III are passes',
      'At least 5 subjects are needed for most next steps',
      'Register on time — usually deadlines in the autumn'
    ],
    page: 'exams-csec',
    link: { text: 'CSEC Exam Guide', page: 'exams-csec' }
  },
  {
    id: 'exams-cape',
    keywords: 'cape advanced level sixth form a levels units unit 1 unit 2 what is cape university preparation',
    type: 'info',
    title: 'CAPE',
    answer: "CAPE is the sixth-form qualification — think A-Levels or first-year university. Students take units over two years to prepare for university.",
    tips: [
      'Each subject has Unit 1 and Unit 2',
      'Most students do 5–7 units across two years',
      'Required for Caribbean university admission',
      'Some universities give credit for CAPE units'
    ],
    page: 'exams-cape',
    link: { text: 'CAPE Exam Guide', page: 'exams-cape' }
  },
  {
    id: 'exams-heart',
    keywords: 'nvqj nvq-j heart vocational training city and guilds city & guilds technical skills trade certification tvet apprenticeship',
    type: 'info',
    title: 'HEART, NVQ-J & Vocational Training',
    answer: "Prefer hands-on, skills-based learning? HEART/NSTA and City & Guilds offer respected vocational qualifications — often free.",
    tips: [
      'NVQ-J levels 1–5 certify practical, work-ready skills',
      'Many HEART programmes are free and include stipends',
      'City & Guilds is internationally recognised',
      'Great routes into trades and technical careers'
    ],
    page: 'exams-nvqj',
    link: { text: 'NVQ-J & HEART Guide', page: 'exams-nvqj' }
  },
  {
    id: 'help-contact',
    keywords: 'help contact support phone number reach you talk to someone who do i call get in touch customer service',
    type: 'info',
    title: 'Get More Help',
    answer: "Happy to point you in the right direction. Here are some useful contacts and where to find more answers.",
    tips: [
      'Ministry of Education: 876-922-1400',
      'CXC local office: 876-960-0340',
      'Browse our Help & FAQs page',
      'You can also contact schools directly'
    ],
    page: 'help',
    link: { text: 'Help & FAQs', page: 'help' }
  }
];

// ---------------------------------------------------------------------------
// 2) CORPUS — current data records made searchable
// ---------------------------------------------------------------------------
function toUrl(website) {
  if (!website || typeof website !== 'string') return null;
  const w = website.trim();
  if (/^https?:\/\//i.test(w)) return w;
  // Only treat it as a link if it looks like a domain (not "Apply at ...")
  if (/\s/.test(w) || !/\.[a-z]{2,}/i.test(w)) return null;
  return `https://${w}`;
}

function joinList(v) {
  return Array.isArray(v) ? v.filter(Boolean).join(' ') : (v || '');
}

// Some datasets use arrays, some use keyed objects — normalise to an array.
function asArray(v) {
  if (Array.isArray(v)) return v;
  if (v && typeof v === 'object') return Object.values(v).flat();
  return [];
}

function buildCorpus() {
  const docs = [];

  const pushScholarship = (item, category) => {
    if (!item || !item.name) return;
    docs.push({
      category,
      title: item.name,
      provider: item.provider || '',
      summary: item.description || `${category} for ${joinList(item.level) || 'students'}`,
      amount: item.amount || (Array.isArray(item.coverage) ? item.coverage[0] : '') || '',
      deadline: item.deadline || '',
      keywords: [category, item.type, item.provider, joinList(item.fields), joinList(item.level), joinList(item.eligibility)]
        .filter(Boolean).join(' '),
      url: toUrl(item.website),
      page: 'funding'
    });
  };

  asArray(scholarshipsData.scholarships).forEach((s) => pushScholarship(s, 'scholarship'));
  asArray(scholarshipsData.grants).forEach((s) => pushScholarship(s, 'grant'));
  asArray(scholarshipsData.loans).forEach((s) => pushScholarship(s, 'student loan'));
  asArray(scholarshipsData.study_abroad).forEach((s) => pushScholarship(s, 'study abroad'));
  if (scholarshipsData.jamvat) pushScholarship(scholarshipsData.jamvat, 'tuition voucher');

  // Tertiary institutions
  asArray(tertiary.data).forEach((i) => {
    if (!i || !i.institution_name) return;
    const programmes = (i.notable_programmes || []).map((p) => p && p.name).filter(Boolean);
    docs.push({
      category: 'university',
      title: i.institution_name,
      provider: i.short_name || '',
      summary: i.description || '',
      amount: '',
      deadline: i.application_deadline || '',
      keywords: [i.short_name, i.institution_type, i.parish, joinList(i.degrees_offered), programmes.join(' ')]
        .filter(Boolean).join(' '),
      url: toUrl(i.website),
      page: 'tertiary'
    });
  });

  return docs;
}

export const CORPUS = buildCorpus();

// ---------------------------------------------------------------------------
// 3) Matching — token-overlap intent routing (built for natural language)
//    plus Fuse for the data corpus and a Fuse fallback for typos.
// ---------------------------------------------------------------------------

// Generic words that carry no intent — dropped before matching.
const STOPWORDS = new Set([
  'i', 'a', 'an', 'the', 'to', 'of', 'my', 'me', 'is', 'are', 'am', 'be', 'do',
  'does', 'did', 'how', 'can', 'could', 'would', 'should', 'with', 'and', 'or',
  'in', 'on', 'at', 'about', 'please', 'tell', 'give', 'want', 'get', 'there',
  'some', 'any', 'it', 'that', 'this', 'what', 'which', 'who', 'when', 'where',
  'why', 'still', 'so', 'if', 'was', 'were', 'have', 'has', 'you', 'your', 'we',
  'they', 'them', 'from', 'into', 'as', 'but', 'not', 'no', 'yes', 'us', 'our'
]);

// Very light stemmer — folds common suffixes so "paying"→"pay", "jobs"→"job".
function stem(word) {
  let w = word;
  if (w.length > 4 && w.endsWith('ies')) return w.slice(0, -3) + 'y';
  if (w.length > 4 && w.endsWith('ing')) return w.slice(0, -3);
  if (w.length > 4 && w.endsWith('ed')) return w.slice(0, -2);
  if (w.length > 3 && w.endsWith('es')) return w.slice(0, -2);
  if (w.length > 3 && w.endsWith('s')) return w.slice(0, -1);
  return w;
}

function tokenize(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter((w) => w && !STOPWORDS.has(w));
}

function stemSet(text) {
  return new Set(tokenize(text).map(stem));
}

// Pre-index topics as stemmed token sets, and compute document frequency so
// distinctive words (abuse, nurse, dropped) outweigh common ones (school).
const TOPIC_INDEX = TOPICS.map((t) => ({
  topic: t,
  tokens: stemSet(`${t.title} ${t.keywords}`),
  titleTokens: stemSet(t.title)
}));
const DF = {};
TOPIC_INDEX.forEach(({ tokens }) => tokens.forEach((tok) => { DF[tok] = (DF[tok] || 0) + 1; }));
const TOPIC_N = TOPIC_INDEX.length;
function idf(tok) {
  return Math.log((TOPIC_N + 1) / ((DF[tok] || 0) + 1)) + 0.25;
}

// Vocabulary of known topic words, used to gently auto-correct typos so a
// query like "scholership" still finds the "scholarship" topic.
const VOCAB = [];
TOPIC_INDEX.forEach(({ tokens }) => tokens.forEach((t) => { if (!VOCAB.includes(t)) VOCAB.push(t); }));

function editDistance(a, b) {
  const m = a.length, n = b.length;
  if (Math.abs(m - n) > 2) return 99;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = a[i - 1] === b[j - 1]
        ? prev[j - 1]
        : 1 + Math.min(prev[j - 1], prev[j], cur[j - 1]);
    }
    prev = cur;
  }
  return prev[n];
}

function canonical(tok) {
  if (tok.length < 4 || VOCAB.includes(tok)) return tok;
  const maxD = tok.length > 6 ? 2 : 1;
  let best = tok, bestD = maxD + 1;
  for (const v of VOCAB) {
    if (Math.abs(v.length - tok.length) > maxD) continue;
    const d = editDistance(tok, v);
    if (d < bestD) { bestD = d; best = v; }
  }
  return bestD <= maxD ? best : tok;
}

function scoreTopics(query) {
  // Auto-correct typos on the raw word first, then stem (correcting after
  // stemming can over-match, e.g. "nursing"→"nurs"→"nurse").
  const qTokens = [...new Set(tokenize(query).map(canonical).map(stem))];
  if (!qTokens.length) return [];
  return TOPIC_INDEX
    .map(({ topic, tokens, titleTokens }) => {
      let score = 0;
      let hits = 0;
      for (const qt of qTokens) {
        if (tokens.has(qt)) {
          // A word that appears in the topic's own title is a stronger signal
          // than one buried in its keyword list (breaks cross-topic ties).
          score += idf(qt) + (titleTokens.has(qt) ? 0.8 : 0);
          hits += 1;
        }
      }
      return { topic, score, hits };
    })
    .filter((r) => r.hits > 0)
    .sort((a, b) => b.score - a.score);
}

const dataFuse = new Fuse(CORPUS, {
  keys: [
    { name: 'title', weight: 3 },
    { name: 'keywords', weight: 2 },
    { name: 'provider', weight: 1.5 },
    { name: 'summary', weight: 1 }
  ],
  includeScore: true,
  ignoreLocation: true,
  threshold: 0.42,
  minMatchCharLength: 3
});

// Fuse over topics — only used as a typo-tolerant fallback.
const topicsFuse = new Fuse(TOPICS, {
  keys: [{ name: 'keywords', weight: 3 }, { name: 'title', weight: 2 }],
  includeScore: true,
  ignoreLocation: true,
  threshold: 0.4,
  minMatchCharLength: 3
});

// ---------------------------------------------------------------------------
// 4) answerQuery — compose a human, helpful response
// ---------------------------------------------------------------------------
function formatResults(items) {
  return items.map((it) => {
    const bits = [];
    if (it.amount) bits.push(it.amount);
    if (it.deadline) bits.push(`Deadline: ${it.deadline}`);
    return {
      category: it.category,
      title: it.title,
      subtitle: it.provider,
      note: bits.join(' · '),
      url: it.url || null,
      page: it.page || null
    };
  });
}

const CATEGORY_LINK = {
  scholarship: { text: 'All scholarships & funding', page: 'funding' },
  grant: { text: 'All scholarships & funding', page: 'funding' },
  'student loan': { text: 'All scholarships & funding', page: 'funding' },
  'study abroad': { text: 'All scholarships & funding', page: 'funding' },
  'tuition voucher': { text: 'All scholarships & funding', page: 'funding' },
  university: { text: 'Universities & Colleges', page: 'tertiary' }
};

// Topics whose answers pair naturally with concrete data records
const DATA_LINKED_PAGES = new Set(['funding', 'tertiary']);

function topicResponse(topic, extra) {
  return {
    type: topic.type || 'info',
    title: topic.title,
    answer: topic.answer,
    tips: topic.tips,
    steps: topic.steps,
    link: topic.link,
    disclaimer: topic.disclaimer,
    ...extra
  };
}

export function answerQuery(rawQuery) {
  const q = (rawQuery || '').trim();
  if (!q) return null;

  const topicScores = scoreTopics(q);
  const best = topicScores[0] || null;

  // Relevant current data (scholarships, opportunities, institutions)
  const dataItems = dataFuse.search(q)
    .filter((r) => r.score <= 0.48)
    .slice(0, 5)
    .map((r) => r.item);

  // Confident intent (a distinctive word or two matched) → lead with the topic,
  // and attach concrete data when the topic pairs with records.
  if (best && best.score >= 1.1) {
    const extra = (dataItems.length && DATA_LINKED_PAGES.has(best.topic.page))
      ? { results: formatResults(dataItems) }
      : {};
    return topicResponse(best.topic, extra);
  }

  // No confident intent, but we found relevant records → show them.
  if (dataItems.length) {
    return {
      type: 'info',
      title: 'Here are some options',
      answer: `Here's what I found related to “${q}”. Tap any item to learn more or apply.`,
      results: formatResults(dataItems),
      link: CATEGORY_LINK[dataItems[0].category] || { text: 'Browse the site', page: 'home' }
    };
  }

  // Weak-but-present intent still beats nothing.
  if (best && best.score > 0) {
    return topicResponse(best.topic);
  }

  // Typo-tolerant fallback: let Fuse try the topics before giving up.
  const fuzzy = topicsFuse.search(q)[0];
  if (fuzzy && fuzzy.score <= 0.45) {
    return topicResponse(fuzzy.item);
  }

  // Friendly fallback with real directions
  return {
    type: 'info',
    title: "Let's find the right help",
    answer: "I didn't quite catch that, but I can point you to the right place. Here are some things people often ask me about:",
    related: [
      { text: 'Scholarships & funding', page: 'funding' },
      { text: 'Careers', page: 'career-path' },
      { text: 'Jobs in education', page: 'education-jobs' },
      { text: 'Special education support', page: 'special-education' },
      { text: 'Child protection', page: 'child-protection' },
      { text: 'Exams (PEP, CSEC, CAPE)', page: 'exams' },
      { text: 'Universities & colleges', page: 'tertiary' },
      { text: 'Second-chance education', page: 'second-chance' }
    ],
    link: { text: 'Visit Help & FAQs', page: 'help' }
  };
}

export default answerQuery;
